import type { ReactNode } from "react";

interface AnswerBlockProps {
  question: string;
  answer: ReactNode;
  bullets?: string[];
  /** Used by Speakable schema selectors. */
  id?: string;
  className?: string;
}

/**
 * Answer-first content card optimized for AI/answer engines and humans.
 * Renders a clear Q + concise A above the fold. Pair with Speakable
 * JSON-LD targeting the same id.
 */
export function AnswerBlock({
  question,
  answer,
  bullets,
  id = "answer-block",
  className = "",
}: AnswerBlockProps) {
  return (
    <aside
      id={id}
      className={`relative my-8 overflow-hidden rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-7 ${className}`}
      data-answer-block
    >
      <span className="absolute inset-y-0 left-0 w-1 bg-primary" aria-hidden="true" />
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
        Quick answer
      </p>
      <h2 className="mt-2 font-display text-xl tracking-tight md:text-2xl" data-speakable="question">
        {question}
      </h2>
      <div className="mt-3 text-base leading-relaxed text-foreground" data-speakable="answer">
        {answer}
      </div>
      {bullets && bullets.length > 0 && (
        <ul className="mt-4 space-y-2 text-sm">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span className="text-foreground/90">{b}</span>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}